import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHero from '@/components/feature/PageHero';
import CtaBanner from '@/components/feature/CtaBanner';
import CategoryTabs from '@/pages/products/components/CategoryTabs';
import ProductBrowser from '@/pages/products/components/ProductBrowser';
import { getCategoryById, getProductsByCategory } from '@/pages/products/productUtils';

const assurances = [
  { id: 'gmp', icon: 'ri-shield-check-line', label: 'Schedule T GMP Certified' },
  { id: 'bulk', icon: 'ri-box-3-line', label: 'Bulk & Wholesale Available' },
  { id: 'delivery', icon: 'ri-truck-line', label: 'Pan-India Dispatch' },
];

export default function ProductCategoryPage() {
  const { category: categoryId } = useParams<{ category: string }>();
  const category = categoryId ? getCategoryById(categoryId) : undefined;

  if (!category) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-20 md:py-28 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="font-heading mt-5 text-2xl md:text-3xl font-semibold text-foreground-950">
            Category Not Found
          </h1>
          <p className="mt-3 text-sm text-foreground-600 max-w-md mx-auto">
            We could not find that product category. Explore our complete Ayurvedic range instead.
          </p>
          <Link
            to="/products"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-base"></i>
            View All Products
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const products = getProductsByCategory(category.id);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Our Products"
        title={category.title}
        description={category.description}
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Products', to: '/products' }, { label: category.name }]}
      />

      <CategoryTabs activeId={category.id} />

      <section className="w-full bg-background-50 border-b border-background-200 py-8 md:py-10">
        <div className="w-full px-4 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {assurances.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-100/60"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <span className="text-sm font-semibold text-foreground-800">{item.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
            <span className="inline-flex items-center gap-2">
              <i className="ri-capsule-line text-primary-600"></i>
              {products.length} formulations in this range
            </span>
            <span className="hidden sm:inline text-background-300">|</span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-information-line text-primary-600"></i>
              Read each approved product label before use
            </span>
          </div>
        </div>
      </section>

      <ProductBrowser categoryId={category.id} categoryName={category.name} />

      <CtaBanner
        eyebrow="Bulk & Distribution"
        title={`Interested in our ${category.name.toLowerCase()} range?`}
        description="Get manufacturer-direct pricing, bulk order support and reliable pan-India supply for your pharmacy, clinic or distribution business."
        primary={{ label: 'Send an Enquiry', to: '/contact', icon: 'ri-send-plane-fill' }}
        secondary={{ label: 'Become a Distributor', to: '/#distributor-apply', icon: 'ri-hand-heart-line' }}
      />
    </SiteLayout>
  );
}